<template>
    <div v-if="displayFooter" :class="['footer-section', 'portfolio-footer', 'ufo-sec', portfolio.portfolioFooter.styleSettings.boxAlign, {'fixed-links': disableAlignmentForLinks}]"
        :style="[{'--section-padding-top': portfolio.portfolioFooter.style && portfolio.portfolioFooter.style.paddingTop ?portfolio.portfolioFooter.style.paddingTop + 'px' : null,
                   '--section-padding-bottom' : portfolio.portfolioFooter.style && portfolio.portfolioFooter.style.paddingBottom ? portfolio.portfolioFooter.style.paddingBottom + 'px' : null}]"
    >
        <ToggleGrid />
        <SectionBackground :section="portfolio.portfolioFooter" />
        <div class="grid-wrapper">
            <div class="grid">
                <div
                    v-for="module in visibleFooterModules"
                    :key="module.moduleId"
                    class="col"
                    :style="columnSettings(module)"
                >
                    <Text v-if="moduleIsText(module) && portfolio.portfolioFooter.displayText && portfolio.portfolioFooter.text?.text" :section="portfolio.portfolioFooter" />
                </div>

                <div v-if="$store.state.portfolio.portfolioFooter.displayDividerLine" class="separator">
                    <AnimationWrapper is-final-section>
                        <template #animationContent>
                            <div class="line" :class="footerLineClasses"></div>
                        </template>
                    </AnimationWrapper>
                </div>
                <div class="footer-links" v-if="showFooterLinks" :class="{'profile_theme_default' : !$store.state.portfolio.templateSettings.theme || $store.state.portfolio.templateSettings.theme.includes('theme_default')}">
                    <div class="link-container">
                        <div v-if="portfolio.portfolioFooter.displayPageLinks && portfolioPages"
                             class="portfolio-pages"
                             :class="portfolio.portfolioFooter.displaySocialLinks && portfolio.socialLinks ? 'portfolio-pages--left' : ''"
                        >
                            <GetHeroNavigationItem v-for="(page, index) in portfolioPages"
                                                   :index="index"
                                                   :page="page"
                                                   :key="page._id"
                                                   :is-in-footer="true"
                            />
                        </div>
                        <div v-if="showSocialLinksInFooterNavigation" class="social-links" :class="portfolio.portfolioFooter.displayPageLinks && portfolioPages ? 'social-links--right' : ''" >
                            <div v-bind:key="socialLink" v-for="(socialLink, index) in portfolio.socialLinks" class="footer-link">
                                <AnimationWrapper
                                    is-final-section
                                    :delay-animation="index ? index * 0.1 : 0"
                                >
                                    <template #animationContent>
                                        <GetHeroSocialLinkItem :social-link="socialLink" />
                                    </template>
                                </AnimationWrapper>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <UXfolioBranding v-if="user.accountSettings.folioLogo && project.type !== 'caseStudy'" class="footer-share" :class="portfolio.templateSettings.theme !== 'profile_theme_nine' ? 'show-color-background' : ''" />
</template>

<script>
import ToggleGrid from "../../../common/components/ToggleGrid.vue";
import AnimationWrapper from "../../../common/components/AnimationWrapper.vue";
import GetHeroNavigationItem from "@/scripts/view/portfolio/common/hero/GetHeroNavigationItem.vue";
import GetHeroSocialLinkItem from "@/scripts/view/portfolio/common/hero/GetHeroSocialLinkItem.vue";
import UXfolioBranding from "@/scripts/view/portfolio/common/UXfolioBranding.vue";
import {mapGetters} from "vuex";
import SectionBackground from "@/scripts/sections/common/SectionBackground.vue";
import {updateSectionLevelColorCssInHead} from "@/scripts/common/TextStyleRenderHelper.js";
import Text from "@/scripts/view/portfolio/sections/modules/Text.vue";
import {ensureFooterLayoutSettings, getVisibleFooterModules} from "@/scripts/common/utilities/footerLayout.js";
import { resolveRowSpan } from "@/scripts/common/utilities/layout.js";
import { SectionModuleTypeEnum } from "@/scripts/common/enums.js";

export default {
    name: "PortfolioFooterSection",
    components: {
        Text,
        SectionBackground,
        UXfolioBranding,
        GetHeroSocialLinkItem,
        GetHeroNavigationItem,
        AnimationWrapper,
        ToggleGrid
    },
    mounted() {
        ensureFooterLayoutSettings(this.viewData.portfolio.portfolioFooter);
        if(!this.$route.query.templatePreview) {
            updateSectionLevelColorCssInHead(this.viewData.portfolio.portfolioFooter);
        }
    },
    computed: {
        ...mapGetters("view", ["viewData"]),
        showSocialLinksInFooterNavigation() {
            return this.portfolio.portfolioFooter.socialIcons?.length < 1
                && this.portfolio.portfolioFooter.displaySocialLinks
                && this.portfolio.socialLinks
        },
        portfolio() {
          return this.viewData.portfolio
        },
        project() {
            return this.viewData.project
        },
        user() {
            return this.viewData.user
        },
        portfolioTemplate() {
            return this.portfolio.templateSettings.theme.replace("_old", "");
        },
        displayFooter() {
            if (this.project.type === 'caseStudy') {
                return this.portfolio.settings.showFooterOnProjects && this.portfolio.portfolioFooter;
            }
            return this.portfolio.settings.showFooterOnPortfolio && this.portfolio.portfolioFooter;
        },
        homeLink() {
            return this.portfolio.ownDomainAddress ? ("https://" + this.portfolio.ownDomainAddress) : ("/" + this.portfolio.shortId)
        },
        //TODO: WHAT IS NOT ALLOWED ?
        notAllowedInThemes() {
            return ['theme_one', 'theme_two', 'theme_three', 'theme_six', 'theme_seven', 'theme_eleven']
        },
        portfolioPages() {
            return this.viewData.portfolioPages
        },
        footerLineClasses() {
            return `${this.$store.state.portfolio.portfolioFooter.lineWeight}`
        },
        showFooterLinks() {
            return this.$store.state.portfolio.portfolioFooter.displayPageLinks || (this.$store.state.portfolio.portfolioFooter.displaySocialLinks && this.portfolio.socialLinks)
        },
        disableAlignmentForLinks() {
            const footer = this.portfolio.portfolioFooter;
            const hasPages = footer.displayPageLinks && this.portfolioPages && this.portfolioPages.length;
            const hasSocials = footer.displaySocialLinks && this.portfolio.socialLinks && this.portfolio.socialLinks.length;
            return hasPages && hasSocials;
        },
        visibleFooterModules() {
            return getVisibleFooterModules(this.portfolio.portfolioFooter);
        },
    },
    methods: {
        moduleIsText(module) {
            return module?.type === SectionModuleTypeEnum.TEXT;
        },
        columnSettings(module) {
            const { rowStart, rowEnd } = resolveRowSpan(module.gridSettings);
            return {
                '--columnStart': module.gridSettings?.columnStart,
                '--columnEnd': module.gridSettings?.columnEnd,
                '--rowStart': rowStart,
                '--rowEnd': rowEnd,
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.button-container {
    &.center {
        display: flex;
        justify-content: center;
    }
    &.right {
        display: flex;
        justify-content: flex-end;
    }
}
</style>
